import type { ComponentProps, FC } from 'react'
import { Suspense, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import type { ChannelFilters } from 'stream-chat'
import type { DefaultStreamChatGenerics } from 'stream-chat-react'
import { ChannelList } from 'stream-chat-react'

import { paths } from 'src/config/paths'
import { useChatChannels } from 'src/features/chats/api/use-chat-channels'
import { SidebarChannelList } from 'src/features/chats/components/channels/channel-list/sidebar-channel-list'
import { SidebarChannelPreview } from 'src/features/chats/components/channels/channel-preview/sidebar-channel-preview'
import { useChatClient } from 'src/features/chats/context/chat-context'
import { useGetNotifications } from 'src/features/notifications/api/use-get-notifications'
import { NotificationSidebarItem } from 'src/features/notifications/components/notification-item/notification-sidebar-item'
import { ErrorBoundaryWrapper } from 'src/shared/components/errors/error-boundary-wrapper'
import { ChatOutline } from 'src/shared/components/icons/chat/chat-outline'
import { NotificationBell } from 'src/shared/components/icons/notification-bell'
import { EmptyNotifications } from 'src/shared/components/icons/notifications/empty-notifications'
import { Button } from 'src/shared/components/ui/buttons/button'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  Sidebar,
  SidebarContent,
} from 'src/shared/components/ui/miscellaneous/sidebar'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useWindowSize } from 'src/shared/hooks/use-window-size'
import { cn } from 'src/shared/utils/cn'

import { ChatsSectionSkeleton, NotificationsSectionSkeleton } from './right-sidebar-skeletons'

const SECTION_HEIGHT = 300
const CARD_HEIGHT = 70

type NotificationsSectionProps = {
  limit?: number
  className?: string
}

const NotificationsSectionContent = ({ className, limit = 5 }: NotificationsSectionProps) => {
  const navigate = useNavigate()
  const { data: notifications } = useGetNotifications({ limit })

  const allGroups = notifications?.pages.flatMap((page) => page.results)

  if (!notifications) {
    return (
      <SidebarGroup className="flex h-[325px] min-h-0 flex-col">
        <SidebarGroupLabel className="mb-2 flex items-center justify-between">
          <div className="flex items-center text-lg font-semibold text-primary">
            <NotificationBell className="mr-2 size-5" />
            Notifications
          </div>
          <Button
            variant="link"
            size="sm"
            onClick={() => navigate(paths.notifications.getHref())}
            className="px-0 text-sm text-primary underline hover:text-muted"
          >
            See all
          </Button>
        </SidebarGroupLabel>
        <SidebarMenu className={cn(className)}>
          <SidebarMenuItem className="flex h-[325px] flex-col items-center justify-center rounded-xl border bg-background p-4 px-10 py-16">
            <EmptyNotifications className="mb-4 size-24 text-[#495954]" />
            <p className="text-center text-sm text-[#495954]">Start interacting with other professionals!</p>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
    )
  }

  return (
    <SidebarGroup className="flex min-h-0 flex-col">
      <SidebarGroupLabel className="mb-3 flex items-center justify-between">
        <div className="flex items-center text-lg font-semibold text-primary">
          <NotificationBell className="mr-2.5 size-5" />
          Notifications
        </div>
        <Button
          variant="link"
          size="sm"
          onClick={() => navigate(paths.notifications.getHref())}
          className="px-0 text-sm text-primary underline hover:text-muted"
        >
          See all
        </Button>
      </SidebarGroupLabel>
      <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border bg-background">
        <div className="flex-1 divide-y divide-border overflow-y-auto">
          {allGroups
            .slice(0, limit)
            .map((group) =>
              group.activities
                .slice(0, 1)
                .map((activity) => <NotificationSidebarItem key={activity.id} activity={activity} group={group} />),
            )}
        </div>
      </div>
    </SidebarGroup>
  )
}

const NotificationsSection = ({ limit = 5, className }: NotificationsSectionProps) => {
  return (
    <Suspense fallback={<NotificationsSectionSkeleton className={className} />}>
      <NotificationsSectionContent limit={limit} className={className} />
    </Suspense>
  )
}

const ChatsSection = ({ className, limit = 5 }: { className?: string; limit?: number }) => {
  const navigate = useNavigate()
  const { data: user } = useMyUserData()
  const { client, isConnecting } = useChatClient()
  const { data: chatIds, isLoading: isChatIdsLoading } = useChatChannels(undefined, [
    'referral',
    'partner-match',
    'network',
  ])

  if (!client || !user || isConnecting || isChatIdsLoading) {
    return <ChatsSectionSkeleton className={className} />
  }

  const filters: ChannelFilters<DefaultStreamChatGenerics> = {
    members: { $in: [user.id.toString()] },
    cid: chatIds ? { $in: chatIds } : { $in: ['messaging:fake-cid'] },
  }

  return (
    <SidebarGroup className="flex min-h-0 flex-col">
      <SidebarGroupLabel className="mb-3 flex items-center justify-between">
        <div className="flex items-center text-lg font-semibold text-primary">
          <ChatOutline className="mr-2.5 size-5" />
          Messages
        </div>
        <Button
          variant="link"
          size="sm"
          onClick={() => navigate(paths.chats.list.getHref())}
          className="px-0 text-sm text-primary underline hover:text-muted"
        >
          See all
        </Button>
      </SidebarGroupLabel>
      <div className={cn('min-h-0 flex-1', className)}>
        <ChannelList
          filters={filters}
          channelRenderFilterFn={(channels) => channels.slice(0, limit)}
          Preview={SidebarChannelPreview}
          List={(listProps) => <SidebarChannelList {...listProps} emptyStateVariant="navbar" />}
        />
      </div>
    </SidebarGroup>
  )
}

type RightSidebarProps = ComponentProps<typeof Sidebar>

const RightSidebar: FC<RightSidebarProps> = ({ ...props }) => {
  const { height } = useWindowSize()

  const limit = useMemo(() => Math.floor((height - SECTION_HEIGHT) / CARD_HEIGHT), [height])

  return (
    <Sidebar {...props} side="right">
      <SidebarContent className="flex h-full flex-col">
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <ErrorBoundaryWrapper
            title="Unable to load chats"
            description="There was an error loading your chats. Please try again later."
          >
            <ChatsSection className="flex-1" limit={limit} />
          </ErrorBoundaryWrapper>

          <ErrorBoundaryWrapper
            title="Unable to load notifications"
            description="There was an error loading your notifications. Please try again later."
          >
            <NotificationsSection limit={limit} />
          </ErrorBoundaryWrapper>
        </div>
      </SidebarContent>
    </Sidebar>
  )
}

export default RightSidebar
